import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { branding } from '../constants/branding';
import { Theme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';

type StockHolding = {
  name: string;
  value: number;
  change: string;
  shares: number;
};

type RealEstateHolding = {
  name: string;
  value: number;
  change: string;
  location: string;
};

type BondHolding = {
  name: string;
  value: number;
  yield: string;
  maturity: string;
  change?: string;
};

type CommodityHolding = {
  name: string;
  value: number;
  change: string;
  weight: string;
};

type AssetHolding = StockHolding | RealEstateHolding | BondHolding | CommodityHolding;

type AssetCategory = {
  id: number;
  name: string;
  value: number;
  change: string;
  color: string;
  icon: string;
  allocation: string;
  holdings: AssetHolding[];
};

type Transaction = {
  id: number;
  type: 'Buy' | 'Sell';
  asset: string;
  amount: string;
  price: number;
  total: number;
  date: string;
  category: string;
  icon: string;
};

type AssetsPortfolio = {
  totalValue: string;
  totalChange: string;
  totalChangeValue: string;
  categories: AssetCategory[];
  transactions: Transaction[];
};

// Sample assets portfolio data
const assetsPortfolio: AssetsPortfolio = {
  totalValue: '$12,000.00',
  totalChange: '+3.2%',
  totalChangeValue: '+$372.00',
  categories: [
    {
      id: 1,
      name: 'Stocks',
      value: 5000.00,
      change: '+4.5%',
      color: '#2ecc71',
      icon: '📈',
      allocation: '41.67%',
      holdings: [
        { name: 'AAPL', value: 2200, change: '+5.2%', shares: 12 },
        { name: 'MSFT', value: 1800, change: '+3.8%', shares: 5 },
        { name: 'GOOGL', value: 1000, change: '+4.1%', shares: 8 },
      ]
    },
    {
      id: 2,
      name: 'Real Estate',
      value: 4000.00,
      change: '+2.1%',
      color: '#e67e22',
      icon: '🏠',
      allocation: '33.33%',
      holdings: [
        { name: 'Property A', value: 2500, change: '+2.5%', location: 'New York' },
        { name: 'Property B', value: 1500, change: '+1.8%', location: 'Miami' },
      ]
    },
    {
      id: 3,
      name: 'Bonds',
      value: 2000.00,
      change: '+1.5%',
      color: '#3498db',
      icon: '📜',
      allocation: '16.67%',
      holdings: [
        { name: 'Government Bonds', value: 1200, yield: '3.5%', maturity: '2025' },
        { name: 'Corporate Bonds', value: 800, yield: '4.2%', maturity: '2026' },
      ]
    },
    {
      id: 4,
      name: 'Commodities',
      value: 1000.00,
      change: '+5.8%',
      color: '#f1c40f',
      icon: '🏆',
      allocation: '8.33%',
      holdings: [
        { name: 'Gold', value: 600, change: '+6.2%', weight: '10 oz' },
        { name: 'Silver', value: 400, change: '+5.1%', weight: '20 oz' },
      ]
    },
  ],
  transactions: [
    {
      id: 1,
      type: 'Buy',
      asset: 'AAPL Stock',
      amount: '5 shares',
      price: 180.50,
      total: 902.50,
      date: '2024-06-01',
      category: 'Stocks',
      icon: '📈',
    },
    {
      id: 2,
      type: 'Sell',
      asset: 'Gold',
      amount: '2 oz',
      price: 1950.00,
      total: 3900.00,
      date: '2024-05-30',
      category: 'Commodities',
      icon: '🏆',
    },
    {
      id: 3,
      type: 'Buy',
      asset: 'Government Bonds',
      amount: '$1000 face value',
      price: 980.00,
      total: 980.00,
      date: '2024-05-29',
      category: 'Bonds',
      icon: '📜',
    },
  ],
};

interface Asset {
  id: string;
  name: string;
  symbol: string;
  type: string;
  price: number;
  currentPrice: number;
  change24h: number;
  dayChange: number;
  dayChangePercentage: number;
  volume24h: number;
  currency: string;
}

interface Order {
  id?: string;
  assetId: string;
  orderType: 'MARKET' | 'LIMIT';
  side: 'BUY' | 'SELL';
  quantity: number;
  price?: number;
  timeInForce: 'DAY' | 'GTC';
  status?: string;
}

// New trading interfaces
interface TradeOrder {
  symbol: string;
  side: 'BUY' | 'SELL';
  orderType: 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT';
  quantity: number;
  price?: number;
  stopPrice?: number;
  timeInForce: 'DAY' | 'GTC' | 'IOC' | 'FOK';
  clientOrderId?: string;
  comment?: string;
}

interface TradeExecution {
  orderId: string;
  symbol: string;
  side: string;
  quantity: number;
  filledQuantity: number;
  price: number;
  status: string;
  timestamp: string;
  commission: number;
  message: string;
}

interface Position {
  symbol: string;
  quantity: number;
  averagePrice: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  unrealizedPnLPercentage: number;
  side: 'LONG' | 'SHORT';
  timestamp: string;
}

interface MarketQuote {
  symbol: string;
  price: number;
  change: number;
  changePercentage: number;
  volume: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  timestamp: string;
}

interface TradeHistory {
  orderId: string;
  symbol: string;
  side: string;
  quantity: number;
  filledQuantity: number;
  price: number;
  status: string;
  timestamp: string;
  commission: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// API Service functions
const API_SERVICES = {
  // Watchlist operations
  addToWatchlist: async (assetId: string, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.post(`/assets/watchlist/${assetId}`);
      return response.data;
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      throw error;
    }
  },

  removeFromWatchlist: async (assetId: string, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.delete(`/assets/watchlist/${assetId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  },

  // Trading operations - Updated to use new endpoints
  executeTrade: async (tradeOrder: TradeOrder, token: string | null): Promise<TradeExecution> => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.post('/assets/trade/execute', tradeOrder);
      return response.data;
    } catch (error) {
      console.error('Error executing trade:', error);
      throw error;
    }
  },

  getTradeStatus: async (orderId: string, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get(`/assets/trade/status/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting trade status:', error);
      throw error;
    }
  },

  cancelTrade: async (orderId: string, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.post(`/assets/trade/cancel/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error canceling trade:', error);
      throw error;
    }
  },

  getOpenPositions: async (token: string | null): Promise<Position[]> => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/trade/positions');
      return response.data;
    } catch (error) {
      console.error('Error fetching open positions:', error);
      throw error;
    }
  },

  getTradeHistory: async (token: string | null, symbol?: string, status?: string, limit: number = 50): Promise<TradeHistory[]> => {
    try {
      if (!token) throw new Error('No authentication token available');
      const params = new URLSearchParams();
      if (symbol) params.append('symbol', symbol);
      if (status) params.append('status', status);
      params.append('limit', limit.toString());
      const response = await axiosInstance.get(`/assets/trade/history?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching trade history:', error);
      throw error;
    }
  },

  getMarketQuote: async (symbol: string, token: string | null): Promise<MarketQuote> => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get(`/assets/market/quote/${symbol}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching market quote:', error);
      throw error;
    }
  },

  getMarketQuotes: async (symbols: string[], token: string | null): Promise<MarketQuote[]> => {
    try {
      if (!token) throw new Error('No authentication token available');
      const params = new URLSearchParams();
      symbols.forEach(symbol => params.append('symbols', symbol));
      const response = await axiosInstance.get(`/assets/market/quotes?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching market quotes:', error);
      throw error;
    }
  },

  // Legacy trading operations (keeping for backward compatibility)
  placeBuyOrder: async (assetType: string, orderData: any, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.post(`/assets/${assetType}/buy`, orderData);
      return response.data;
    } catch (error) {
      console.error('Error placing buy order:', error);
      throw error;
    }
  },

  placeSellOrder: async (assetType: string, orderData: any, token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.post(`/assets/${assetType}/sell`, orderData);
      return response.data;
    } catch (error) {
      console.error('Error placing sell order:', error);
      throw error;
    }
  },

  // Data fetching operations
  fetchWatchlist: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/watchlist');
      return response.data;
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      throw error;
    }
  },

  fetchStocks: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/stocks');
      return response.data;
    } catch (error) {
      console.error('Error fetching stocks:', error);
      throw error;
    }
  },

  fetchETFs: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/etfs');
      return response.data;
    } catch (error) {
      console.error('Error fetching ETFs:', error);
      throw error;
    }
  },

  fetchPortfolio: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/portfolio');
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      throw error;
    }
  },

  fetchOrders: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/orders');
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  fetchCommodities: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/commodities');
      return response.data;
    } catch (error) {
      console.error('Error fetching commodities:', error);
      throw error;
    }
  },

  fetchAnalytics: async (token: string | null) => {
    try {
      if (!token) throw new Error('No authentication token available');
      const response = await axiosInstance.get('/assets/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },
};

// Add sample assets data
const sampleAssets: Asset[] = [
  {
    id: 'AAPL',
    name: 'Apple Inc.',
    symbol: 'AAPL',
    type: 'STOCK',
    price: 175.50,
    currentPrice: 175.50,
    change24h: 2.5,
    dayChange: 2.5,
    dayChangePercentage: 1.45,
    volume24h: 55000000,
    currency: '$'
  },
  {
    id: 'GOOGL',
    name: 'Alphabet Inc.',
    symbol: 'GOOGL',
    type: 'STOCK',
    price: 2800.00,
    currentPrice: 2800.00,
    change24h: -15.0,
    dayChange: -15.0,
    dayChangePercentage: -0.53,
    volume24h: 2100000,
    currency: '$'
  },
  {
    id: 'GLD',
    name: 'SPDR Gold Shares',
    symbol: 'GLD',
    type: 'COMMODITY',
    price: 180.25,
    currentPrice: 180.25,
    change24h: 1.25,
    dayChange: 1.25,
    dayChangePercentage: 0.70,
    volume24h: 8500000,
    currency: '$'
  },
  {
    id: 'VOO',
    name: 'Vanguard S&P 500 ETF',
    symbol: 'VOO',
    type: 'ETF',
    price: 410.75,
    currentPrice: 410.75,
    change24h: 3.25,
    dayChange: 3.25,
    dayChangePercentage: 0.80,
    volume24h: 4200000,
    currency: '$'
  }
];

const Assets: React.FC = () => {
  const theme = useTheme();
  const [timeFilter, setTimeFilter] = useState<'1m' | '3m' | '6m' | '1y' | 'All'>('1m');
  const [portfolio, setPortfolio] = useState<any>(null);
  const [commodities, setCommodities] = useState<Asset[]>([]);
  const [stocks, setStocks] = useState<Asset[]>([]);
  const [etfs, setEtfs] = useState<Asset[]>([]);
  const [watchlist, setWatchlist] = useState<Asset[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [orderDetails, setOrderDetails] = useState<Partial<Order>>({
    orderType: 'MARKET',
    side: 'BUY',
    timeInForce: 'DAY',
  });
  const [isInWatchlist, setIsInWatchlist] = useState<{[key: string]: boolean}>({});
  const [orderStatus, setOrderStatus] = useState<{
    loading: boolean;
    error: string | null;
    success: boolean;
  }>({
    loading: false,
    error: null,
    success: false
  });

  // New trading state
  const [positions, setPositions] = useState<Position[]>([]);
  const [marketQuotes, setMarketQuotes] = useState<MarketQuote[]>([]);
  const [tradeHistory, setTradeHistory] = useState<TradeHistory[]>([]);
  const [activeTab, setActiveTab] = useState<'portfolio' | 'trading' | 'positions' | 'history'>('portfolio');
  const [tradeOrder, setTradeOrder] = useState<Partial<TradeOrder>>({
    orderType: 'MARKET',
    side: 'BUY',
    timeInForce: 'DAY',
    quantity: 1
  });
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [isLoadingTradingData, setIsLoadingTradingData] = useState(false);
  const [tradingError, setTradingError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const token = useAppSelector(state => state.auth.token);

  const loadData = async () => {
    try {
      // Try to fetch from API first
      const [
        portfolioData,
        commoditiesData,
        stocksData,
        etfsData,
        watchlistData,
        ordersData,
        analyticsData
      ] = await Promise.all([
        API_SERVICES.fetchPortfolio(token).catch(() => null),
        API_SERVICES.fetchCommodities(token).catch(() => []),
        API_SERVICES.fetchStocks(token).catch(() => []),
        API_SERVICES.fetchETFs(token).catch(() => []),
        API_SERVICES.fetchWatchlist(token).catch(() => []),
        API_SERVICES.fetchOrders(token).catch(() => []),
        API_SERVICES.fetchAnalytics(token).catch(() => null),
      ]);

      // If API calls fail, use sample data
      setPortfolio(portfolioData || assetsPortfolio);
      
      const stocks = stocksData.length > 0 ? stocksData : sampleAssets.filter(a => a.type === 'STOCK');
      const commodities = commoditiesData.length > 0 ? commoditiesData : sampleAssets.filter(a => a.type === 'COMMODITY');
      const etfs = etfsData.length > 0 ? etfsData : sampleAssets.filter(a => a.type === 'ETF');
      
      setCommodities(commodities);
      setStocks(stocks);
      setEtfs(etfs);
      setWatchlist(watchlistData || []);
      setOrders(ordersData || []);
      setAnalytics(analyticsData || {
        assetAllocation: {
          Stocks: 45,
          ETFs: 25,
          Commodities: 15,
          Bonds: 15
        }
      });

      // Update watchlist status
      const watchlistMap = (watchlistData || []).reduce((acc: any, asset: Asset) => {
        acc[asset.id] = true;
        return acc;
      }, {});
      setIsInWatchlist(watchlistMap);

      // Load trading data
      await loadTradingData();
    } catch (error) {
      console.error('Error loading data:', error);
      
      // Fallback to sample data on error
      setCommodities(sampleAssets.filter(a => a.type === 'COMMODITY'));
      setStocks(sampleAssets.filter(a => a.type === 'STOCK'));
      setEtfs(sampleAssets.filter(a => a.type === 'ETF'));
    }
  };

  const loadTradingData = async () => {
    try {
      setIsLoadingTradingData(true);
      setTradingError(null);

      // Get all unique symbols from assets
      const allAssets = [...stocks, ...etfs, ...commodities];
      const symbols = allAssets.map(asset => asset.symbol);

      // Fetch trading data in parallel
      const [positionsData, marketQuotesData, tradeHistoryData] = await Promise.all([
        API_SERVICES.getOpenPositions(token).catch(() => []),
        API_SERVICES.getMarketQuotes(symbols, token).catch(() => []),
        API_SERVICES.getTradeHistory(token).catch(() => [])
      ]);

      setPositions(positionsData);
      setMarketQuotes(marketQuotesData);
      setTradeHistory(tradeHistoryData);

    } catch (error) {
      console.error('Error loading trading data:', error);
      setTradingError('Failed to load trading data');
    } finally {
      setIsLoadingTradingData(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh trading data every 30 seconds
  useEffect(() => {
    if (activeTab === 'trading' || activeTab === 'positions' || activeTab === 'history') {
      const interval = setInterval(() => {
        loadTradingData();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Refresh trading data when switching to trading-related tabs
  useEffect(() => {
    if (activeTab === 'trading' || activeTab === 'positions' || activeTab === 'history') {
      loadTradingData();
    }
  }, [activeTab]);

  const handleWatchlistToggle = async (asset: Asset) => {
    try {
      if (isInWatchlist[asset.id]) {
        await API_SERVICES.removeFromWatchlist(asset.id, token);
      } else {
        await API_SERVICES.addToWatchlist(asset.id, token);
      }
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    }
  };

  // Quick trade functions - Updated to use new trading API
  const quickMarketBuy = async (asset: Asset, quantity: number) => {
    try {
      setOrderStatus({ loading: true, error: null, success: false });
      
      const tradeOrder: TradeOrder = {
        symbol: asset.symbol,
        side: 'BUY',
        orderType: 'MARKET',
        quantity: quantity,
        timeInForce: 'DAY'
      };

      await API_SERVICES.executeTrade(tradeOrder, token);
      setOrderStatus({ loading: false, error: null, success: true });
      loadData(); // Refresh data
    } catch (error) {
      setOrderStatus({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to place buy order', 
        success: false 
      });
    }
  };

  const quickMarketSell = async (asset: Asset, quantity: number) => {
    try {
      setOrderStatus({ loading: true, error: null, success: false });
      
      const tradeOrder: TradeOrder = {
        symbol: asset.symbol,
        side: 'SELL',
        orderType: 'MARKET',
        quantity: quantity,
        timeInForce: 'DAY'
      };

      await API_SERVICES.executeTrade(tradeOrder, token);
      setOrderStatus({ loading: false, error: null, success: true });
      loadData(); // Refresh data
    } catch (error) {
      setOrderStatus({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to place sell order', 
        success: false 
      });
    }
  };

  // Enhanced order submit with validation - Updated to use new trading API
  const handleOrderSubmit = async () => {
    if (!selectedAsset || !orderDetails.quantity) {
      setOrderStatus({ 
        loading: false, 
        error: 'Please enter quantity', 
        success: false 
      });
      return;
    }

    if (orderDetails.orderType === 'LIMIT' && !orderDetails.price) {
      setOrderStatus({ 
        loading: false, 
        error: 'Please enter limit price', 
        success: false 
      });
      return;
    }

    try {
      setOrderStatus({ loading: true, error: null, success: false });
      
      const tradeOrder: TradeOrder = {
        symbol: selectedAsset.symbol,
        side: orderDetails.side || 'BUY',
        orderType: orderDetails.orderType || 'MARKET',
        quantity: orderDetails.quantity,
        price: orderDetails.price,
        timeInForce: orderDetails.timeInForce || 'DAY'
      };

      await API_SERVICES.executeTrade(tradeOrder, token);
      setOrderStatus({ loading: false, error: null, success: true });
      setOrderDialogOpen(false);
      loadData(); // Refresh data
    } catch (error) {
      setOrderStatus({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to place order', 
        success: false 
      });
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setOrderDetails(prev => ({ ...prev, price: value }));
    }
  };

  // New trading functions
  const executeTradeOrder = async (tradeOrder: TradeOrder) => {
    try {
      setOrderStatus({ loading: true, error: null, success: false });
      await API_SERVICES.executeTrade(tradeOrder, token);
      setOrderStatus({ loading: false, error: null, success: true });
      await loadTradingData(); // Refresh trading data
      return true;
    } catch (error) {
      setOrderStatus({ 
        loading: false, 
        error: error instanceof Error ? error.message : 'Failed to execute trade', 
        success: false 
      });
      return false;
    }
  };

  const cancelTradeOrder = async (orderId: string) => {
    try {
      await API_SERVICES.cancelTrade(orderId, token);
      await loadTradingData(); // Refresh trading data
      return true;
    } catch (error) {
      console.error('Error canceling trade:', error);
      return false;
    }
  };

  const refreshMarketData = async () => {
    await loadTradingData();
  };

  const renderAnalytics = () => {
    // Create fallback data if analytics is not available
    const assetAllocationData = analytics?.assetAllocation || {
      Stocks: 45,
      ETFs: 25,
      Commodities: 15,
      Bonds: 15
    };

    const chartData = Object.entries(assetAllocationData).map(([key, value]) => ({
      name: key,
      value: Number(value),
    }));

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#f5a623', fontWeight: 600 }}>
            Asset Allocation
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }: any) => `${name} ${((percent as number ?? 0) * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any, name: any) => [`${value}%`, name]}
                    labelFormatter={(label) => `${label}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" textAlign="center">
                No allocation data available
              </Typography>
            )}
          </Box>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {chartData.map((entry, index) => (
              <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: COLORS[index % COLORS.length] 
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  {entry.name} ({entry.value}%)
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderAssetTable = () => (
    <TableContainer 
      component={Paper}
      sx={{
        width: '100%',
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: '100%', sm: 650 }
        }
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
            <TableCell align="right">Price</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Change</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...stocks, ...etfs, ...commodities].map((asset) => (
            <TableRow key={asset.id}>
              <TableCell sx={{ maxWidth: { xs: '120px', sm: 'none' } }}>
                <Typography noWrap>
                  {asset.name} ({asset.symbol})
                </Typography>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{asset.type}</TableCell>
              <TableCell align="right">{asset.currency} {asset.currentPrice.toFixed(2)}</TableCell>
              <TableCell 
                align="right"
                sx={{
                  display: { xs: 'none', sm: 'table-cell' },
                  color: asset.dayChange >= 0 ? '#2ecc71' : '#e74c3c',
                }}
              >
                {asset.dayChange >= 0 ? '+' : ''}{asset.dayChangePercentage.toFixed(2)}%
              </TableCell>
              <TableCell align="right">
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  justifyContent: 'flex-end',
                  flexWrap: { xs: 'nowrap', sm: 'wrap' }
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleWatchlistToggle(asset)}
                    sx={{ 
                      minWidth: { xs: '32px', sm: '40px' },
                      px: { xs: 0.5, sm: 1.5 }
                    }}
                  >
                    {isInWatchlist[asset.id] ? '★' : '☆'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setSelectedAsset(asset);
                      setOrderDialogOpen(true);
                    }}
                    sx={{
                      minWidth: { xs: '60px', sm: 'auto' },
                      px: { xs: 1, sm: 2 }
                    }}
                  >
                    Trade
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Enhanced order dialog
  const renderOrderDialog = () => (
    <Dialog 
      open={orderDialogOpen} 
      onClose={() => {
        setOrderDialogOpen(false);
        setOrderStatus({ loading: false, error: null, success: false });
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {orderDetails.side} {selectedAsset?.name}
        {selectedAsset && ` (${selectedAsset.symbol})`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {selectedAsset && (
            <Typography variant="subtitle1" gutterBottom>
              Current Price: {selectedAsset.currency} {selectedAsset.currentPrice.toFixed(2)}
            </Typography>
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Order Type</InputLabel>
            <Select
              value={orderDetails.orderType}
              onChange={(event: SelectChangeEvent) => 
                setOrderDetails(prev => ({ ...prev, orderType: event.target.value as 'MARKET' | 'LIMIT' }))
              }
            >
              <MenuItem value="MARKET">Market</MenuItem>
              <MenuItem value="LIMIT">Limit</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Side</InputLabel>
            <Select
              value={orderDetails.side}
              onChange={(event: SelectChangeEvent) => 
                setOrderDetails(prev => ({ ...prev, side: event.target.value as 'BUY' | 'SELL' }))
              }
            >
              <MenuItem value="BUY">Buy</MenuItem>
              <MenuItem value="SELL">Sell</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Quantity"
            type="number"
            value={orderDetails.quantity || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setOrderDetails({ ...orderDetails, quantity: Number(e.target.value) })}
            InputProps={{
              inputProps: { min: 0 },
              endAdornment: selectedAsset?.type === 'COMMODITY' ? (
                <InputAdornment position="end">oz</InputAdornment>
              ) : (
                <InputAdornment position="end">shares</InputAdornment>
              )
            }}
          />

          {orderDetails.orderType === 'LIMIT' && (
            <TextField
              fullWidth
              margin="normal"
              label="Limit Price"
              type="number"
              value={orderDetails.price || ''}
              onChange={handlePriceChange}
              InputProps={{
                startAdornment: selectedAsset?.currency && (
                  <InputAdornment position="start">{selectedAsset.currency}</InputAdornment>
                ),
                inputProps: { min: 0, step: 0.01 }
              }}
            />
          )}

          {orderStatus.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {orderStatus.error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            setOrderDialogOpen(false);
            setOrderStatus({ loading: false, error: null, success: false });
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleOrderSubmit} 
          variant="contained" 
          color="primary"
          disabled={orderStatus.loading}
        >
          {orderStatus.loading ? (
            <CircularProgress size={24} />
          ) : (
            `Place ${orderDetails.side} Order`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Add success notification
  const renderNotification = () => (
    <Snackbar
      open={orderStatus.success || !!orderStatus.error}
      autoHideDuration={6000}
      onClose={() => setOrderStatus({ loading: false, error: null, success: false })}
    >
      <Alert
        onClose={() => setOrderStatus({ loading: false, error: null, success: false })}
        severity={orderStatus.success ? 'success' : 'error'}
        sx={{ width: '100%' }}
      >
        {orderStatus.success ? 'Order placed successfully!' : orderStatus.error}
      </Alert>
    </Snackbar>
  );

  // New trading UI components
  const renderPositionsTable = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#f5a623', fontWeight: 600 }}>
            Open Positions
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={refreshMarketData}
            disabled={isLoadingTradingData}
            startIcon={isLoadingTradingData ? <CircularProgress size={16} /> : null}
            sx={{
              borderColor: '#f5a623',
              color: '#f5a623',
              '&:hover': {
                borderColor: '#e69512',
                bgcolor: 'rgba(245, 166, 35, 0.04)'
              }
            }}
          >
            {isLoadingTradingData ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
        {isLoadingTradingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : positions.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No open positions
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell align="right">Current Price</TableCell>
                  <TableCell align="right">Market Value</TableCell>
                  <TableCell align="right">Unrealized P&L</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((position) => (
                  <TableRow key={position.symbol}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {position.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={position.side === 'LONG' ? 'success.main' : 'error.main'}
                        fontWeight={600}
                      >
                        {position.side}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{position.quantity}</TableCell>
                    <TableCell align="right">${position.averagePrice.toFixed(2)}</TableCell>
                    <TableCell align="right">${position.currentPrice.toFixed(2)}</TableCell>
                    <TableCell align="right">${position.marketValue.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={position.unrealizedPnL >= 0 ? 'success.main' : 'error.main'}
                        fontWeight={600}
                      >
                        {position.unrealizedPnL >= 0 ? '+' : ''}${position.unrealizedPnL.toFixed(2)}
                        <br />
                        ({position.unrealizedPnLPercentage >= 0 ? '+' : ''}{position.unrealizedPnLPercentage.toFixed(2)}%)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedSymbol(position.symbol);
                          setTradeOrder({
                            symbol: position.symbol,
                            side: position.side === 'LONG' ? 'SELL' : 'BUY',
                            orderType: 'MARKET',
                            quantity: position.quantity,
                            timeInForce: 'DAY'
                          });
                          setActiveTab('trading');
                        }}
                      >
                        Close
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderTradeHistoryTable = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#f5a623', fontWeight: 600 }}>
            Trade History
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={refreshMarketData}
            disabled={isLoadingTradingData}
            startIcon={isLoadingTradingData ? <CircularProgress size={16} /> : null}
            sx={{
              borderColor: '#f5a623',
              color: '#f5a623',
              '&:hover': {
                borderColor: '#e69512',
                bgcolor: 'rgba(245, 166, 35, 0.04)'
              }
            }}
          >
            {isLoadingTradingData ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
        {isLoadingTradingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : tradeHistory.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No trade history available
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Commission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tradeHistory.slice(0, 20).map((trade) => (
                  <TableRow key={trade.orderId}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {trade.orderId.slice(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {trade.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={trade.side === 'BUY' ? 'success.main' : 'error.main'}
                        fontWeight={600}
                      >
                        {trade.side}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{trade.quantity}</TableCell>
                    <TableCell align="right">${trade.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          trade.status === 'FILLED' ? 'success.main' :
                          trade.status === 'CANCELLED' ? 'error.main' : 'warning.main'
                        }
                      >
                        {trade.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(trade.timestamp).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">${trade.commission.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  const renderTradingInterface = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#f5a623', fontWeight: 600 }}>
            Advanced Trading
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={refreshMarketData}
            disabled={isLoadingTradingData}
            startIcon={isLoadingTradingData ? <CircularProgress size={16} /> : null}
            sx={{
              borderColor: '#f5a623',
              color: '#f5a623',
              '&:hover': {
                borderColor: '#e69512',
                bgcolor: 'rgba(245, 166, 35, 0.04)'
              }
            }}
          >
            {isLoadingTradingData ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Market Data */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Market Data
            </Typography>
            {selectedSymbol && marketQuotes.find(q => q.symbol === selectedSymbol) ? (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" fontWeight={600}>{selectedSymbol}</Typography>
                <Typography variant="h5" color="primary" fontWeight={600}>
                  ${marketQuotes.find(q => q.symbol === selectedSymbol)?.price.toFixed(2)}
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    (marketQuotes.find(q => q.symbol === selectedSymbol)?.change || 0) >= 0 
                      ? 'success.main' 
                      : 'error.main'
                  }
                >
                  {(marketQuotes.find(q => q.symbol === selectedSymbol)?.change || 0) >= 0 ? '+' : ''}
                  ${(marketQuotes.find(q => q.symbol === selectedSymbol)?.change || 0).toFixed(2)} 
                  ({(marketQuotes.find(q => q.symbol === selectedSymbol)?.changePercentage || 0).toFixed(2)}%)
                </Typography>
              </Box>
            ) : (
              <Typography color="text.secondary">Select a symbol to view market data</Typography>
            )}
          </Box>

          {/* Trading Form */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Place Order
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Symbol</InputLabel>
                <Select
                  value={tradeOrder.symbol || ''}
                  onChange={(e) => {
                    setTradeOrder({ ...tradeOrder, symbol: e.target.value });
                    setSelectedSymbol(e.target.value);
                  }}
                  label="Symbol"
                >
                  {[...stocks, ...etfs, ...commodities].map((asset) => (
                    <MenuItem key={asset.symbol} value={asset.symbol}>
                      {asset.symbol} - {asset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Side</InputLabel>
                <Select
                  value={tradeOrder.side || 'BUY'}
                  onChange={(e) => setTradeOrder({ ...tradeOrder, side: e.target.value as 'BUY' | 'SELL' })}
                  label="Side"
                >
                  <MenuItem value="BUY">Buy</MenuItem>
                  <MenuItem value="SELL">Sell</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={tradeOrder.orderType || 'MARKET'}
                  onChange={(e) => setTradeOrder({ ...tradeOrder, orderType: e.target.value as any })}
                  label="Order Type"
                >
                  <MenuItem value="MARKET">Market</MenuItem>
                  <MenuItem value="LIMIT">Limit</MenuItem>
                  <MenuItem value="STOP">Stop</MenuItem>
                  <MenuItem value="STOP_LIMIT">Stop Limit</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Quantity"
                type="number"
                value={tradeOrder.quantity || ''}
                onChange={(e) => setTradeOrder({ ...tradeOrder, quantity: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">#</InputAdornment>,
                }}
              />

              {(tradeOrder.orderType === 'LIMIT' || tradeOrder.orderType === 'STOP_LIMIT') && (
                <TextField
                  fullWidth
                  size="small"
                  label="Price"
                  type="number"
                  value={tradeOrder.price || ''}
                  onChange={(e) => setTradeOrder({ ...tradeOrder, price: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              )}

              {(tradeOrder.orderType === 'STOP' || tradeOrder.orderType === 'STOP_LIMIT') && (
                <TextField
                  fullWidth
                  size="small"
                  label="Stop Price"
                  type="number"
                  value={tradeOrder.stopPrice || ''}
                  onChange={(e) => setTradeOrder({ ...tradeOrder, stopPrice: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={() => executeTradeOrder(tradeOrder as TradeOrder)}
                disabled={!tradeOrder.symbol || !tradeOrder.quantity || orderStatus.loading}
                sx={{
                  bgcolor: '#f5a623',
                  '&:hover': { bgcolor: '#e69512' }
                }}
              >
                {orderStatus.loading ? <CircularProgress size={20} color="inherit" /> : 'Execute Trade'}
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderTabs = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { key: 'portfolio', label: 'Portfolio' },
            { key: 'trading', label: 'Trading' },
            { key: 'positions', label: 'Positions' },
            { key: 'history', label: 'History' }
          ].map((tab) => (
            <Button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              sx={{
                color: activeTab === tab.key ? 'white' : 'text.primary',
                bgcolor: activeTab === tab.key ? '#f5a623' : 'transparent',
                '&:hover': {
                  bgcolor: activeTab === tab.key ? '#e69512' : 'rgba(0, 0, 0, 0.04)',
                },
                textTransform: 'none',
                borderRadius: 1,
                px: 2,
                py: 1,
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        maxWidth: '960px',
        width: '100%',
        mx: 'auto',
        px: { xs: 1, sm: 3 },
        pt: { xs: 2, sm: 3 },
        pb: { xs: 7, sm: 3 },
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.5rem', mb: 1 }}>Asset Portfolio</Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary" 
          sx={{ 
            fontSize: '0.875rem',
            mb: { xs: 1, sm: 0 }
          }}
        >
          Track and manage your traditional investments
        </Typography>
      </Box>

      {/* Tabs */}
      {renderTabs()}

      {/* Content based on active tab */}
      {activeTab === 'portfolio' && (
        <>
          {/* Time Filter */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'flex-start'
            }}>
              {['1m', '3m', '6m', '1y', 'All'].map((period) => (
                <Button
                  key={period}
                  onClick={() => setTimeFilter(period as typeof timeFilter)}
                  sx={{
                    minWidth: { xs: '35px', sm: '40px' },
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    fontSize: '0.875rem',
                    color: timeFilter === period ? 'white' : 'text.primary',
                    bgcolor: timeFilter === period ? '#f5a623' : 'transparent',
                    '&:hover': {
                      bgcolor: timeFilter === period ? '#e69512' : 'rgba(0, 0, 0, 0.04)',
                    },
                    textTransform: 'none',
                    borderRadius: 1,
                  }}
                >
                  {period}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Main Portfolio Card */}
          <Card 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #f5a623 0%, #f7b84b 100%)',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              mx: { xs: -0.5, sm: 0 },
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Box>
                  <Typography variant="subtitle2" color="white" sx={{ mb: 0.5, opacity: 0.9 }}>
                    Total Portfolio Value
                  </Typography>
                  <Typography variant="h4" color="white" sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', sm: '2rem' } }}>
                    ${portfolio?.totalValue || '12,000.00'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="subtitle2" color="white" sx={{ mb: 0.5, opacity: 0.9 }}>
                    Total Change
                  </Typography>
                  <Typography variant="h4" color="white" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                    {portfolio?.totalChange || '+3.2%'} 
                    <Typography component="span" color="white" sx={{ ml: 1, fontSize: { xs: '0.875rem', sm: '1rem' }, opacity: 0.9 }}>
                      ({portfolio?.totalChangeValue || '+$372.00'})
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Asset Categories Grid */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: { xs: 1, sm: 2, md: 2.5 },
            mb: 3,
            mx: { xs: -0.5, sm: 0 },
          }}>
            {assetsPortfolio.categories.map((category) => (
              <Card 
                key={category.id}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                  bgcolor: 'white',
                }}
              >
                <CardContent sx={{ 
                  p: { xs: 1.5, sm: 2 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2 } }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ mr: 1, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>{category.icon}</Typography>
                    <Box sx={{ 
                      flex: 1,
                      minWidth: 0,
                      mr: 1
                    }}>
                      <Typography 
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' }, 
                          fontWeight: 600, 
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {category.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary', 
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      >
                        Allocation: {category.allocation}
                      </Typography>
                    </Box>
                    <Typography sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      color: category.change.startsWith('+') ? '#2ecc71' : '#e74c3c',
                      whiteSpace: 'nowrap',
                      pl: 0.5
                    }}>
                      ${category.value.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {category.holdings.map((holding, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: { xs: 0.5, sm: 0.75 },
                          borderBottom: index < category.holdings.length - 1 ? '1px solid #f0f0f0' : 'none',
                        }}
                      >
                        <Box sx={{ 
                          minWidth: 0, 
                          flex: 1,
                          mr: 1.5
                        }}>
                          <Typography 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                              fontWeight: 500, 
                              color: 'text.primary',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {holding.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary', 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' }
                            }}
                          >
                            {('shares' in holding) ? `${holding.shares} shares` :
                             ('location' in holding) ? holding.location :
                             ('maturity' in holding) ? `Matures ${holding.maturity}` :
                             ('weight' in holding) ? holding.weight : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                            fontWeight: 600,
                            color: 'text.primary'
                          }}>
                            ${holding.value.toFixed(2)}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            color: ('change' in holding && holding.change?.startsWith('+')) ? '#2ecc71' : '#e74c3c',
                            fontWeight: 500
                          }}>
                            {('change' in holding && holding.change) ? holding.change : 
                             ('yield' in holding) ? `Yield: ${holding.yield}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Analytics */}
          {renderAnalytics()}

          {/* Available Assets */}
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            mx: { xs: -0.5, sm: 0 },
            mb: 3
          }}>
            <CardContent>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>Available Assets</Typography>
              {renderAssetTable()}
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            mx: { xs: -0.5, sm: 0 },
            mb: { xs: 7, sm: 3 }
          }}>
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
                Transaction History
              </Typography>
              <TableContainer sx={{ 
                width: '100%',
                '& .MuiTable-root': {
                  minWidth: { xs: '100%', sm: 650 }
                }
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          py: 1.5,
                          width: { xs: '20%', sm: 'auto' }
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          py: 1.5,
                          width: { xs: '30%', sm: 'auto' }
                        }}
                      >
                        Asset
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          py: 1.5,
                          width: { xs: '25%', sm: 'auto' }
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          py: 1.5,
                          width: { xs: '25%', sm: 'auto' }
                        }}
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assetsPortfolio.transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell sx={{ 
                          py: 1.5,
                          width: { xs: '20%', sm: 'auto' }
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <Typography sx={{ fontSize: '1rem' }}>{transaction.icon}</Typography>
                            <Typography 
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                fontWeight: 500,
                                color: transaction.type === 'Buy' ? '#2ecc71' : '#e74c3c',
                              }}
                            >
                              {transaction.type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 1.5,
                          width: { xs: '30%', sm: 'auto' }
                        }}>
                          <Typography 
                            noWrap 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              maxWidth: { xs: '80px', sm: 'none' }
                            }}
                          >
                            {transaction.asset}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ 
                          py: 1.5,
                          width: { xs: '25%', sm: 'auto' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}>
                          {transaction.amount}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{
                            py: 1.5,
                            width: { xs: '25%', sm: 'auto' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontWeight: 500,
                            color: transaction.type === 'Buy' ? '#e74c3c' : '#2ecc71',
                          }}
                        >
                          {transaction.type === 'Buy' ? '-' : '+'}${transaction.total.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Trading Interface */}
      {activeTab === 'trading' && renderTradingInterface()}

      {/* Positions Table */}
      {activeTab === 'positions' && renderPositionsTable()}

      {/* Trade History Table */}
      {activeTab === 'history' && renderTradeHistoryTable()}

      {/* Dialogs and Notifications */}
      {renderOrderDialog()}
      {renderNotification()}
    </Box>
  );
};

export default Assets; 